import React, { useEffect, useMemo, useState } from 'react'
import { App, Button, Card, Flex, Popconfirm, Space, Typography } from 'antd'
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  PlusOutlined
} from '@ant-design/icons'
import { gold, green, red } from '@ant-design/colors'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../types/company/Company'
import { MappingLevel, MappingMatrixRow, SourceMap } from '../../types'
import { getMappingLevel, isCellComplete } from '../../utils/mappingLevel'
import { buildMappingMatrix } from '../../utils/buildMappingMatrix'
import { findSourceMapRecords } from '../../utils/findSourceMapRecords'
import { buildSourceMapIdentifiers } from '../../utils/sourceMapPayload'
import { useSaveSourceMap } from '../../queries/useSaveSourceMap'
import { useDeleteSourceMap } from '../../queries/useDeleteSourceMap'
import MappingExitLink from '../MappingExitLink'
import CompanyList, { StatusInfo } from './CompanyList'
import RecordRow, { RecordFormState } from './RecordRow'

interface PairMappingEditorProps {
  companies: Company[]
  sourceMaps: SourceMap[]
  rows?: MappingMatrixRow[]
  loading?: boolean
  initialOwnerId?: string
  initialCounterpartyId?: string
}

const recordToForm = (record: SourceMap, fallbackLocalCompanyId: string): RecordFormState => ({
  id: record.id,
  localCompanyId:
    record.identifiers.find(identifier => identifier.identifierType === 'local')?.value ?? fallbackLocalCompanyId,
  sourceId: record.identifiers.find(identifier => identifier.identifierType === 'source')?.value ?? ''
})

const emptyForm = (localCompanyId: string): RecordFormState => ({ localCompanyId, sourceId: '' })

// Source ids picked by rows other than `currentIndex`. Passed to the select so the same source
// record can't be chosen twice for one pair+role (a meaningless duplicate, not genuine one-to-many).
const sourcesUsedElsewhere = (forms: RecordFormState[], currentIndex: number): string[] =>
  forms
    .filter((_, i) => i !== currentIndex)
    .map(form => form.sourceId)
    .filter(Boolean)

const ICON_COMPLETE = <CheckCircleFilled style={{ color: green[3] }} />
const ICON_PARTIAL = <ExclamationCircleFilled style={{ color: gold[3] }} />
const ICON_MISSING = <CloseCircleFilled style={{ color: red[3] }} />

const LEVEL_ICON: Partial<Record<MappingLevel, React.ReactNode>> = {
  complete: ICON_COMPLETE,
  customerOnly: ICON_PARTIAL,
  supplierOnly: ICON_PARTIAL,
  missing: ICON_MISSING
}

const PairMappingEditor: React.FC<PairMappingEditorProps> = ({
  companies,
  sourceMaps,
  rows,
  loading,
  initialOwnerId,
  initialCounterpartyId
}) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const saveSourceMap = useSaveSourceMap()
  const deleteSourceMap = useDeleteSourceMap()

  const [ownerId, setOwnerId] = useState(initialOwnerId ?? companies[0]?.id ?? '')
  const [counterpartyId, setCounterpartyId] = useState(
    initialCounterpartyId ?? companies.find(company => company.id !== (initialOwnerId ?? companies[0]?.id))?.id ?? ''
  )
  const owner = companies.find(company => company.id === ownerId)
  const counterparty = companies.find(company => company.id === counterpartyId)

  const [customerForms, setCustomerForms] = useState<RecordFormState[]>([])
  const [supplierForms, setSupplierForms] = useState<RecordFormState[]>([])

  const customerRecords = useMemo(
    () => findSourceMapRecords(sourceMaps, ownerId, counterpartyId, 'Customer'),
    [sourceMaps, ownerId, counterpartyId]
  )
  const supplierRecords = useMemo(
    () => findSourceMapRecords(sourceMaps, ownerId, counterpartyId, 'Supplier'),
    [sourceMaps, ownerId, counterpartyId]
  )

  const matrixRows = useMemo(
    () => rows ?? buildMappingMatrix(companies, sourceMaps).rows,
    [rows, companies, sourceMaps]
  )

  const rowByCompanyId = useMemo(() => new Map(matrixRows.map(row => [row.company.id, row])), [matrixRows])

  useEffect(() => {
    setCustomerForms(
      customerRecords.length
        ? customerRecords.map(record => recordToForm(record, counterpartyId))
        : [emptyForm(counterpartyId)]
    )
    setSupplierForms(
      supplierRecords.length
        ? supplierRecords.map(record => recordToForm(record, counterpartyId))
        : [emptyForm(counterpartyId)]
    )
  }, [customerRecords, supplierRecords, counterpartyId])

  useEffect(() => {
    if (!ownerId && companies[0]) setOwnerId(companies[0].id)
  }, [companies, ownerId])

  useEffect(() => {
    if (ownerId && (!counterpartyId || counterpartyId === ownerId)) {
      setCounterpartyId(companies.find(company => company.id !== ownerId)?.id ?? '')
    }
  }, [ownerId, counterpartyId, companies])

  const getPairStatus = (fromId: string, toId: string): MappingLevel | undefined => {
    const cell = rowByCompanyId.get(fromId)?.cells.find(c => c.counterpartyId === toId)
    return cell ? getMappingLevel(cell) : undefined
  }

  const getOwnerSummary = (companyId: string): StatusInfo | undefined => {
    const cells = rowByCompanyId.get(companyId)?.cells.filter(cell => !cell.isSelf)
    if (!cells || cells.length === 0) return undefined

    const levels = cells.map(getMappingLevel)
    const completeCount = levels.filter(level => level === 'complete').length
    let level: MappingLevel = 'customerOnly'
    if (completeCount === levels.length) level = 'complete'
    else if (completeCount === 0) level = 'missing'
    return { icon: LEVEL_ICON[level] }
  }

  const orderedPairs = useMemo(
    () =>
      matrixRows.flatMap(row =>
        row.cells
          .filter(cell => !cell.isSelf)
          .map(cell => ({
            ownerId: row.company.id,
            counterpartyId: cell.counterpartyId,
            complete: isCellComplete(cell)
          }))
      ),
    [matrixRows]
  )

  const goToNextMissing = () => {
    const currentIndex = orderedPairs.findIndex(
      pair => pair.ownerId === ownerId && pair.counterpartyId === counterpartyId
    )
    const rest = [...orderedPairs.slice(currentIndex + 1), ...orderedPairs.slice(0, currentIndex + 1)]
    const next = rest.find(pair => !pair.complete)
    if (next) {
      setOwnerId(next.ownerId)
      setCounterpartyId(next.counterpartyId)
    }
  }

  const saveForms = (forms: RecordFormState[], sourceEntity: 'Customer' | 'Supplier') => {
    if (!owner) return []
    return forms
      .filter(form => (form.localCompanyId && form.sourceId) || form.id)
      .map(form => {
        if (form.localCompanyId && form.sourceId) {
          return saveSourceMap.mutateAsync({
            id: form.id,
            silent: true,
            payload: {
              companyId: owner.id,
              source: 'FINADECK',
              localEntity: 'Company',
              sourceEntity,
              identifiers: buildSourceMapIdentifiers(form.localCompanyId, form.sourceId)
            }
          })
        }
        // Existing record with a cleared source selection: delete rather than silently drop.
        return deleteSourceMap.mutateAsync({ id: form.id as number, silent: true })
      })
  }

  // One user action (the save button) can trigger several create/update/delete calls under the
  // hood (customer row + supplier row, both possibly changed) - each mutation stays silent and this
  // shows a single toast for the whole action instead of one per underlying call.
  const handleSave = async () => {
    const results = await Promise.all([
      ...saveForms(customerForms, 'Customer'),
      ...saveForms(supplierForms, 'Supplier')
    ])
    // Both form arrays can be all-empty rows (no sourceId, no id) - saveForms filters those out, so
    // don't claim success when nothing was actually sent.
    if (results.length > 0) {
      message.success(t('messages:updateSuccess', 'Tiedot tallennettu onnistuneesti'))
    }
  }

  const handleAddRow = (role: 'customer' | 'supplier') => {
    const setForms = role === 'customer' ? setCustomerForms : setSupplierForms
    setForms(prev => [...prev, emptyForm(counterpartyId)])
  }

  const handleDeleteRow = async (role: 'customer' | 'supplier', index: number) => {
    const forms = role === 'customer' ? customerForms : supplierForms
    const setForms = role === 'customer' ? setCustomerForms : setSupplierForms
    const row = forms[index]
    if (row?.id) await deleteSourceMap.mutateAsync({ id: row.id })
    setForms(prev => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [emptyForm(counterpartyId)]
    })
  }

  const handleDeletePair = async () => {
    const ids = [...customerForms, ...supplierForms].map(form => form.id).filter((id): id is number => !!id)
    await Promise.all(ids.map(id => deleteSourceMap.mutateAsync({ id, silent: true })))
    message.success(t('messages:deleteSuccess', 'Poistettu'))
    setCustomerForms([emptyForm(counterpartyId)])
    setSupplierForms([emptyForm(counterpartyId)])
  }

  const hasPairRecords = customerRecords.length > 0 || supplierRecords.length > 0

  return (
    <>
      <MappingExitLink />
      <Card
        loading={loading}
        size="default"
        style={{ width: 'fit-content' }}
        title={t('groupCustomerMapping:pairTitle', 'Asiakaslinkitys · pari')}
      >
        <Flex gap={16} align="flex-start" wrap="wrap">
          <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid #f0f0f0' }}>
            <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>
              {t('groupCustomerMapping:pairOwner', 'Kirjanpitävä yhtiö')}
            </Typography.Text>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <CompanyList
                companies={companies}
                selectedId={ownerId}
                onSelect={setOwnerId}
                getStatus={getOwnerSummary}
              />
            </div>
          </div>
          <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid #f0f0f0' }}>
            <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>
              {t('groupCustomerMapping:pairCounterparty', 'Vastapuoli')}
            </Typography.Text>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <CompanyList
                companies={companies.filter(company => company.id !== ownerId)}
                selectedId={counterpartyId}
                onSelect={setCounterpartyId}
                getStatus={companyId => {
                  const level = ownerId ? getPairStatus(ownerId, companyId) : undefined
                  return level ? { icon: LEVEL_ICON[level] } : undefined
                }}
              />
            </div>
          </div>
          <div style={{ flex: 1, maxWidth: 500 }}>
            {owner && counterparty && (
              <>
                <Flex align="baseline" gap={10} style={{ marginBottom: 4 }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {owner.name} → {counterparty.name}
                  </Typography.Title>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
                  {t(
                    'groupCustomerMapping:pairDescription',
                    '{{owner}} tarvitsee sekä asiakas- että toimittajatiedon yhtiölle {{counterparty}}.',
                    { owner: owner.name, counterparty: counterparty.name }
                  )}
                </Typography.Text>

                <Flex vertical gap={8} style={{ marginBottom: 8 }}>
                  {customerForms.map((form, index) => (
                    <RecordRow
                      key={form.id ?? `new-customer-${index}`}
                      role={index === 0 ? t('groupCustomerMapping:pairCustomerRole', 'Asiakas') : undefined}
                      form={form}
                      onChange={updated =>
                        setCustomerForms(prev => prev.map((row, i) => (i === index ? updated : row)))
                      }
                      onDelete={() => handleDeleteRow('customer', index)}
                      deleting={deleteSourceMap.isPending}
                      roleKey="customer"
                      companyId={ownerId}
                      excludeValues={sourcesUsedElsewhere(customerForms, index)}
                    />
                  ))}
                  <Button block type="dashed" icon={<PlusOutlined />} onClick={() => handleAddRow('customer')}>
                    {t('groupCustomerMapping:pairAddCustomer', 'lisää asiakas')}
                  </Button>
                </Flex>

                <Flex vertical gap={8} style={{ marginBottom: 8 }}>
                  {supplierForms.map((form, index) => (
                    <RecordRow
                      key={form.id ?? `new-supplier-${index}`}
                      role={index === 0 ? t('groupCustomerMapping:pairSupplierRole', 'Toimittaja') : undefined}
                      form={form}
                      onChange={updated =>
                        setSupplierForms(prev => prev.map((row, i) => (i === index ? updated : row)))
                      }
                      onDelete={() => handleDeleteRow('supplier', index)}
                      deleting={deleteSourceMap.isPending}
                      roleKey="supplier"
                      companyId={ownerId}
                      excludeValues={sourcesUsedElsewhere(supplierForms, index)}
                    />
                  ))}
                  <Button block type="dashed" icon={<PlusOutlined />} onClick={() => handleAddRow('supplier')}>
                    {t('groupCustomerMapping:pairAddSupplier', 'lisää toimittaja')}
                  </Button>
                </Flex>
                <div style={{ borderTop: '1px solid #e4e0d8' }} />

                <Flex justify="space-between" style={{ marginTop: 14 }}>
                  <div>
                    {hasPairRecords && (
                      <Popconfirm
                        title={t('groupCustomerMapping:pairDeletePairConfirm', 'Poistetaanko koko pari?')}
                        onConfirm={handleDeletePair}
                        okText={t('global:yes', 'Kyllä')}
                        cancelText={t('global:no', 'Ei')}
                      >
                        <Button danger loading={deleteSourceMap.isPending}>
                          {t('groupCustomerMapping:pairDeletePair', 'Poista linkitykset')}
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                  <Space>
                    <Button icon={<ArrowRightOutlined />} iconPosition="end" onClick={goToNextMissing}>
                      {t('groupCustomerMapping:pairNextMissing', 'Seuraava puuttuva')}
                    </Button>
                    <Button type="primary" onClick={handleSave} loading={saveSourceMap.isPending}>
                      {t('groupCustomerMapping:pairSave', 'Tallenna pari')}
                    </Button>
                  </Space>
                </Flex>
              </>
            )}
          </div>
        </Flex>
      </Card>
    </>
  )
}

export default PairMappingEditor
